import React from 'react';
import { Plus } from 'lucide-react';
import { useMobileNavigate, useMobileNavigationAction } from './MobileNavigationGuard';

import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { NotificationIcon } from '../notifications/NotificationIcon';
import { MessagesIcon } from '../messages/inbox/MessagesIcon';
import { GlobalSearchLauncher } from '../search/GlobalSearchLauncher';
import { TooltipProvider } from '../ui/tooltip';
import { UserMenu } from '../userMenu/UserMenu';
import { useUserMenu } from '../userMenu/useUserMenu';
import UserProfileDialog from '../UserProfileDialog';
import SettingsDialog from '../SettingsDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useModalState } from '../../hooks/useModalState';
import { QuickCreateContactDialog } from '../records/dialogs/QuickCreateContactDialog';

type MobileHeaderProps = {
  onLogoPress?: () => void;
  navigationButtonRef?: React.Ref<HTMLButtonElement>;
};

export function MobileHeader({ onLogoPress, navigationButtonRef }: MobileHeaderProps) {
  const { user, redirectToLogin, isProfileOpen, closeProfile } = useAuth();
  const userMenu = useUserMenu();
  const requestNavigation = useMobileNavigationAction();
  const navigate = useMobileNavigate();
  const createDialog = useModalState();
  const quickContactDialog = useModalState();

  const handleCreateNavigate = React.useCallback((target: 'deal' | 'task' | 'service' | 'contact') => {
    createDialog.close();
    switch (target) {
      case 'deal':
        navigate('/deals/new');
        break;
      case 'task':
        navigate('/tasks?new=1');
        break;
      case 'service':
        navigate('/service-maintenance?new=1');
        break;
      case 'contact':
        quickContactDialog.open();
        break;
      default:
        break;
    }
  }, [createDialog, navigate, quickContactDialog]);

  return (
    <>
      <header className="bg-background/95 sticky top-0 z-40 border-b border-border shadow-sm backdrop-blur supports-[backdrop-filter]:backdrop-blur">
        <div className="flex items-center justify-between px-3 pb-2 safe-area-pt-2">
          <div className="flex shrink-0 items-center gap-[2px] [&>button]:h-[44px] [&>button]:w-[44px]">
            <button
              type="button"
              aria-label="Open navigation"
              ref={navigationButtonRef}
              className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-full hover:bg-muted transition-colors"
              onClick={onLogoPress}
            >
              <img
                src="/favicon.png"
                alt="RH Tools"
                className="h-6 w-6 select-none object-contain"
                draggable={false}
              />
            </button>
          </div>
          <TooltipProvider delayDuration={100}>
            <div className="flex shrink-0 items-center gap-[2px] [&>button]:h-[44px] [&>button]:w-[44px]">
              <GlobalSearchLauncher />
              <button
                type="button"
                className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                onClick={createDialog.open}
                aria-label="Create new record"
              >
                <Plus className="h-5 w-5" />
              </button>
              <NotificationIcon />
              <MessagesIcon />
              {user ? (
                <UserMenu {...userMenu} compact logout={() => requestNavigation(userMenu.logout)} />
              ) : (
                <Button size="sm" className="h-8 px-3 text-xs font-semibold" onClick={() => redirectToLogin()}>
                  Sign in
                </Button>
              )}
            </div>
          </TooltipProvider>
        </div>
      </header>
      <UserProfileDialog open={isProfileOpen} onOpenChange={closeProfile} />
      <SettingsDialog
        open={userMenu.isSettingsOpen}
        onOpenChange={userMenu.setIsSettingsOpen}
        defaultTab={userMenu.settingsTab}
      />
      <Dialog open={createDialog.isOpen} onOpenChange={createDialog.setOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-sm rounded-lg">
          <DialogHeader>
            <DialogTitle>New record</DialogTitle>
            <DialogDescription>Select what you want to create.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Button
              type="button"
              variant="secondary"
              className="w-full justify-start gap-3 h-12"
              onClick={() => handleCreateNavigate('deal')}
            >
              <span className="text-xs font-mono text-muted-foreground">1</span>
              New Deal
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="w-full justify-start gap-3 h-12"
              onClick={() => handleCreateNavigate('task')}
            >
              <span className="text-xs font-mono text-muted-foreground">2</span>
              New Task
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="w-full justify-start gap-3 h-12"
              onClick={() => handleCreateNavigate('service')}
            >
              <span className="text-xs font-mono text-muted-foreground">3</span>
              New Service
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="w-full justify-start gap-3 h-12"
              onClick={() => handleCreateNavigate('contact')}
            >
              <span className="text-xs font-mono text-muted-foreground">4</span>
              New Contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <QuickCreateContactDialog
        open={quickContactDialog.isOpen}
        onOpenChange={quickContactDialog.setOpen}
      />
    </>
  );
}
